import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Hidden,
  Toolbar,
  Typography
} from '@material-ui/core';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Gap } from 'components';
import { PrivacyPolicy, TermsOfService } from './shared';
import leaveRemainingImage from '../images/leave-remaining.jpg';
import leaveRequestsImage from '../images/leave-requests.jpg';
import profileImage from '../images/profile.jpg';
import promoImage from '../images/promo4.jpg';
import logo from '../images/logo.png';

const StyledToolbar = styled(Toolbar)`
  display: flex;
`;

const HeaderSpacer = styled(Box)`
  flex: 1;
`;

const HeaderBrand = styled(Button)`
  color: ${p => p.theme.palette.primary.contrastText};
  padding: 0;
  min-width: 0;
  text-transform: none;

  &:hover {
    background: transparent;
  }
`;

const HeaderBrandLabel = styled(Typography)`
  margin-left: ${p => p.theme.spacing(2)}px;
`;

const ContrastButton = styled(Button)`
  color: ${p => p.theme.palette.primary.contrastText};
  margin-right: ${p => p.theme.spacing(4)}px;
`;

const CalloutButton = styled(Button)`
  border-radius: 20px;
  padding: ${p => p.theme.spacing(1, 2)};
  background-color: #ffffff;
  color: ${p => p.theme.palette.primary.dark};
`;

const ToolbarOffset = styled.div`
  min-height: 56px;

  ${p => p.theme.breakpoints.up('sm')} {
    min-height: 64px;
  }
`;

const MarketingAppBar = styled(AppBar)`
  transition: box-shadow 160ms ease;
`;

const Hero = styled(Box)`
  background: linear-gradient(
    180deg,
    ${p => p.theme.palette.primary.main} 0%,
    #2b79bc 45%,
    #1f5c95 100%
  );
  color: #fff;
  padding: ${p => p.theme.spacing(8, 0, 10)};
`;

const SectionBase = ({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) => <Box className={className}>{children}</Box>;

const Section = styled(SectionBase)<{ alternate?: boolean }>`
  padding: ${p => p.theme.spacing(8, 0)};
  background: ${p => (p.alternate ? '#f7f9fc' : '#ffffff')};
`;

const Screenshot = styled.img`
  width: 100%;
  border-radius: 12px;
  box-shadow: ${p => p.theme.shadows[3]};
`;

const FeatureCard = styled(Card)`
  height: 100%;
  display: flex;
`;

const CardBody = styled(CardContent)`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const LinkButton = styled(Button)`
  margin-right: ${p => p.theme.spacing(2)}px;
  margin-bottom: ${p => p.theme.spacing(2)}px;
`;

const HeroOutlineButton = styled(LinkButton)`
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.72);

  &:hover {
    border-color: #ffffff;
    background-color: rgba(255, 255, 255, 0.12);
  }
`;

const MarketingCardButton = styled(Button)`
  margin-top: auto;
  align-self: flex-start;
`;

const BulletList = styled.ul`
  margin: 0;
  padding-left: ${p => p.theme.spacing(3)}px;
`;

const NumberedList = styled.ol`
  margin: 0;
  padding-left: ${p => p.theme.spacing(3)}px;

  li {
    margin-bottom: ${p => p.theme.spacing(1)}px;
  }
`;

const ServerAddress = styled.code`
  display: inline-block;
  font-family: 'Roboto Mono', 'SF Mono', Menlo, Consolas, monospace;
  font-size: 1rem;
  background: #eef3fa;
  color: ${p => p.theme.palette.primary.dark};
  border: 1px solid #d6e2f2;
  border-radius: 8px;
  padding: ${p => p.theme.spacing(1.5, 2)};
  word-break: break-all;
`;

type MarketingCardItem = {
  title: string;
  description: string;
  href: string;
  image: string;
  linkLabel?: string;
};

export const marketingRoutes = [
  '/pricing',
  '/signup',
  '/mcp-setup',
  '/features',
  '/features/leave-management',
  '/features/expense-claims',
  '/features/onboarding',
  '/compare/personio',
  '/compare/bamboohr',
  '/compare/spreadsheets',
  '/startup-hr-software',
  '/leave-management-for-small-teams',
  '/expense-claims-for-startups',
  '/employee-onboarding-software'
];

const MarketingLinks = ({
  primaryHref,
  primaryLabel,
  secondaryHref,
  secondaryLabel
}: {
  primaryHref: string;
  primaryLabel: string;
  secondaryHref?: string;
  secondaryLabel?: string;
}) => (
  <Box mt={4}>
    <LinkButton variant="contained" color="secondary" href={primaryHref}>
      {primaryLabel}
    </LinkButton>
    {secondaryHref && secondaryLabel && (
      <HeroOutlineButton variant="outlined" href={secondaryHref}>
        {secondaryLabel}
      </HeroOutlineButton>
    )}
  </Box>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <>
    <Typography variant="h4" color="primary">
      {children}
    </Typography>
    <Gap size={2} />
  </>
);

const MarketingCardGrid = ({
  sectionTitle,
  cards,
  alternate
}: {
  sectionTitle: string;
  cards: MarketingCardItem[];
  alternate?: boolean;
}) => (
  <Section alternate={alternate}>
    <Container>
      <SectionTitle>{sectionTitle}</SectionTitle>
      <Grid container spacing={3}>
        {cards.map(card => (
          <Grid item xs={12} md={4} key={card.href}>
            <FeatureCard>
              <CardBody>
                <Screenshot src={card.image} alt={card.title} />
                <Gap size={2} />
                <Typography variant="h5" color="primary">
                  {card.title}
                </Typography>
                <Gap />
                <Typography variant="body1">{card.description}</Typography>
                <Gap size={2} />
                <MarketingCardButton
                  color="primary"
                  href={card.href}
                  variant="outlined"
                >
                  {card.linkLabel || card.title}
                </MarketingCardButton>
              </CardBody>
            </FeatureCard>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Section>
);

const MarketingHeader = ({
  pricingHref,
  signupHref
}: {
  pricingHref: string;
  signupHref: string;
}) => {
  const { t } = useTranslation();
  const [raised, setRaised] = useState(() => window.scrollY > 0);

  useEffect(() => {
    const syncRaised = () => {
      setRaised(window.scrollY > 0);
    };

    syncRaised();
    window.addEventListener('scroll', syncRaised, { passive: true });

    return () => {
      window.removeEventListener('scroll', syncRaised);
    };
  }, []);

  return (
    <>
      <MarketingAppBar elevation={raised ? 4 : 0} position="fixed">
        <Container>
          <StyledToolbar>
            <HeaderBrand href="/">
              <img src={logo} alt={t('logo')} height={48} width={48} />
              <Hidden smDown>
                <HeaderBrandLabel variant="h5">{t('spotHr')}</HeaderBrandLabel>
              </Hidden>
            </HeaderBrand>
            <HeaderSpacer />
            <Hidden smDown>
              <ContrastButton href="/features">
                {t('marketingNavFeatures')}
              </ContrastButton>
              <ContrastButton href={pricingHref}>
                {t('marketingNavPricing')}
              </ContrastButton>
            </Hidden>
            <ContrastButton href="/signin">{t('signin')}</ContrastButton>
            <CalloutButton href={signupHref} variant="contained">
              {t('signup')}
            </CalloutButton>
          </StyledToolbar>
        </Container>
      </MarketingAppBar>
      <ToolbarOffset />
    </>
  );
};

export function PricingPage() {
  const { t } = useTranslation();
  const { search } = window.location;

  return (
    <>
      <MarketingHeader
        pricingHref={`/pricing${search}`}
        signupHref="#pricing-plans"
      />
      <Hero>
        <Container>
          <Typography variant="overline">{t('pricingPageEyebrow')}</Typography>
          <Typography variant="h2">{t('pricingPageTitle')}</Typography>
          <Gap />
          <Typography variant="h6">{t('pricingPageSubtitle')}</Typography>
          <MarketingLinks
            primaryHref="#pricing-plans"
            primaryLabel={t('pricingPageHeroCta')}
            secondaryHref="/features"
            secondaryLabel={t('marketingViewFeatures')}
          />
        </Container>
      </Hero>
      <Section>
        <Container id="pricing-plans">
          <Grid container spacing={3}>
            {['small', 'startup', 'large'].map(size => (
              <Grid item xs={12} md={4} key={size}>
                <FeatureCard>
                  <CardBody>
                    <Typography variant="h4" color="primary">
                      {t(size)}
                    </Typography>
                    <Gap />
                    <Typography variant="body1">{t(`${size}Desc`)}</Typography>
                    <Gap />
                    <Typography variant="h4">{t(`${size}Price`)}</Typography>
                    <Typography variant="caption">{t('perMonth')}</Typography>
                    <Gap />
                    <Typography variant="body2">
                      {t('thirtyDaysFree')}
                    </Typography>
                    <Gap size={2} />
                    <MarketingCardButton
                      color="secondary"
                      href={`/checkout/${size}${search}`}
                      variant="contained"
                    >
                      {t('pricingPagePickPlan')}
                    </MarketingCardButton>
                  </CardBody>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Section>
      <Section alternate>
        <Container>
          <SectionTitle>{t('pricingPageIncludedTitle')}</SectionTitle>
          <BulletList>
            <li>{t('pricingPageIncluded1')}</li>
            <li>{t('pricingPageIncluded2')}</li>
            <li>{t('pricingPageIncluded3')}</li>
          </BulletList>
        </Container>
      </Section>
      <Section>
        <Container>
          <SectionTitle>{t('pricingPageFaqTitle')}</SectionTitle>
          <Typography variant="h6">{t('pricingFaq1Question')}</Typography>
          <Typography variant="body1">{t('pricingFaq1Answer')}</Typography>
          <Gap size={3} />
          <Typography variant="h6">{t('pricingFaq2Question')}</Typography>
          <Typography variant="body1">{t('pricingFaq2Answer')}</Typography>
        </Container>
      </Section>
    </>
  );
}

export function FeaturesPage() {
  const { t } = useTranslation();
  const { search } = window.location;

  const cards = [
    {
      title: t('leave'),
      description: t('featuresPageLeaveDesc'),
      href: '/features/leave-management',
      image: leaveRequestsImage
    },
    {
      title: t('expenses'),
      description: t('featuresPageExpensesDesc'),
      href: '/features/expense-claims',
      image: promoImage
    },
    {
      title: t('onboarding'),
      description: t('featuresPageOnboardingDesc'),
      href: '/features/onboarding',
      image: profileImage
    }
  ];
  const comparisonCards = [
    {
      title: t('comparisonPagePersonioTitle'),
      description: t('comparisonPagePersonioSubtitle'),
      href: '/compare/personio',
      image: promoImage
    },
    {
      title: t('comparisonPageBambooHrTitle'),
      description: t('comparisonPageBambooHrSubtitle'),
      href: '/compare/bamboohr',
      image: profileImage
    },
    {
      title: t('comparisonPageSpreadsheetsTitle'),
      description: t('comparisonPageSpreadsheetsSubtitle'),
      href: '/compare/spreadsheets',
      image: leaveRequestsImage
    }
  ];
  const useCaseCards = [
    {
      title: t('useCasePageStartupTitle'),
      description: t('useCasePageStartupSubtitle'),
      href: '/startup-hr-software',
      image: promoImage
    },
    {
      title: t('useCasePageLeaveTitle'),
      description: t('useCasePageLeaveSubtitle'),
      href: '/leave-management-for-small-teams',
      image: leaveRemainingImage
    },
    {
      title: t('useCasePageExpenseTitle'),
      description: t('useCasePageExpenseSubtitle'),
      href: '/expense-claims-for-startups',
      image: leaveRequestsImage
    },
    {
      title: t('useCasePageOnboardingTitle'),
      description: t('useCasePageOnboardingSubtitle'),
      href: '/employee-onboarding-software',
      image: profileImage
    }
  ];

  return (
    <>
      <MarketingHeader
        pricingHref={`/pricing${search}`}
        signupHref={`/signup${search}`}
      />
      <Hero>
        <Container>
          <Typography variant="overline">{t('featuresPageEyebrow')}</Typography>
          <Typography variant="h2">{t('featuresPageTitle')}</Typography>
          <Gap />
          <Typography variant="h6">{t('featuresPageSubtitle')}</Typography>
          <MarketingLinks
            primaryHref="/pricing"
            primaryLabel={t('marketingCtaPrimary')}
          />
        </Container>
      </Hero>
      <MarketingCardGrid
        sectionTitle={t('featuresPagePrimaryLinksTitle')}
        cards={cards}
      />
      <Section alternate>
        <Container>
          <SectionTitle>{t('featuresPageSecondaryTitle')}</SectionTitle>
          <Typography variant="body1">
            {t('featuresPageSecondaryCopy')}
          </Typography>
        </Container>
      </Section>
      <MarketingCardGrid
        sectionTitle={t('marketingComparisonLinksTitle')}
        cards={comparisonCards}
      />
      <MarketingCardGrid
        sectionTitle={t('marketingUseCaseLinksTitle')}
        cards={useCaseCards}
        alternate
      />
    </>
  );
}

const BlogLink = styled.a`
  color: ${p => p.theme.palette.primary.main};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const MarketingLandingPage = ({
  eyebrow,
  title,
  subtitle,
  workflowTitle,
  workflowItems,
  outcome,
  image,
  imageAlt,
  primaryLabel,
  secondaryLabel,
  secondaryHref,
  relatedTitle,
  relatedCards,
  blogHref,
  blogLinkText
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
  workflowTitle: string;
  workflowItems: string[];
  outcome: string;
  image: string;
  imageAlt: string;
  primaryLabel: string;
  secondaryLabel: string;
  secondaryHref: string;
  relatedTitle: string;
  relatedCards: MarketingCardItem[];
  // Optional contextual deep-link to the matching Spot HR Blog article. The
  // anchor text is the (English) post title so the link is descriptive for
  // search engines; the lead-in label is localised.
  blogHref?: string;
  blogLinkText?: string;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <MarketingHeader pricingHref="/pricing" signupHref="/signup" />
      <Hero>
        <Container>
          <Typography variant="overline">{eyebrow}</Typography>
          <Typography variant="h2">{title}</Typography>
          <Gap />
          <Typography variant="h6">{subtitle}</Typography>
          <MarketingLinks
            primaryHref="/pricing"
            primaryLabel={primaryLabel}
            secondaryHref={secondaryHref}
            secondaryLabel={secondaryLabel}
          />
        </Container>
      </Hero>
      <Section>
        <Container>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Screenshot src={image} alt={imageAlt} />
            </Grid>
            <Grid item xs={12} md={5}>
              <SectionTitle>{workflowTitle}</SectionTitle>
              <BulletList>
                {workflowItems.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </BulletList>
              <Gap size={3} />
              <Typography variant="body1">{outcome}</Typography>
              {blogHref && blogLinkText && (
                <>
                  <Gap size={2} />
                  <Typography variant="body1">
                    {`${t('relatedBlogLabel')}: `}
                    <BlogLink
                      href={blogHref}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {blogLinkText}
                    </BlogLink>
                  </Typography>
                </>
              )}
            </Grid>
          </Grid>
        </Container>
      </Section>
      <MarketingCardGrid
        sectionTitle={relatedTitle}
        cards={relatedCards}
        alternate
      />
    </>
  );
};

export function LeaveManagementFeaturePage() {
  const { t } = useTranslation();

  return (
    <MarketingLandingPage
      eyebrow={t('featurePageLeaveEyebrow')}
      blogHref="https://blog.spothr.net/blog/how-to-manage-employee-leave-in-a-small-business-without-spreadsheets/"
      blogLinkText="How to Manage Employee Leave in a Small Business Without Spreadsheets"
      title={t('featurePageLeaveTitle')}
      subtitle={t('featurePageLeaveSubtitle')}
      workflowTitle={t('featurePageLeaveWorkflowTitle')}
      workflowItems={[
        t('featurePageLeaveWorkflow1'),
        t('featurePageLeaveWorkflow2'),
        t('featurePageLeaveWorkflow3')
      ]}
      outcome={t('featurePageLeaveOutcome')}
      image={leaveRemainingImage}
      imageAlt={t('featurePageLeaveTitle')}
      primaryLabel={t('marketingSeePricing')}
      secondaryLabel={t('marketingViewFeatures')}
      secondaryHref="/features"
      relatedTitle={t('marketingRelatedPagesTitle')}
      relatedCards={[
        {
          title: t('useCasePageLeaveTitle'),
          description: t('useCasePageLeaveSubtitle'),
          href: '/leave-management-for-small-teams',
          image: leaveRequestsImage
        },
        {
          title: t('comparisonPageSpreadsheetsTitle'),
          description: t('comparisonPageSpreadsheetsSubtitle'),
          href: '/compare/spreadsheets',
          image: leaveRequestsImage
        },
        {
          title: t('comparisonPagePersonioTitle'),
          description: t('comparisonPagePersonioSubtitle'),
          href: '/compare/personio',
          image: promoImage
        }
      ]}
    />
  );
}

export function ExpenseClaimsFeaturePage() {
  const { t } = useTranslation();

  return (
    <MarketingLandingPage
      eyebrow={t('featurePageExpensesEyebrow')}
      blogHref="https://blog.spothr.net/blog/how-to-set-up-an-expense-claim-approval-process-for-a-small-business/"
      blogLinkText="How to Set Up an Expense Claim Approval Process for a Small Business"
      title={t('featurePageExpensesTitle')}
      subtitle={t('featurePageExpensesSubtitle')}
      workflowTitle={t('featurePageExpensesWorkflowTitle')}
      workflowItems={[
        t('featurePageExpensesWorkflow1'),
        t('featurePageExpensesWorkflow2'),
        t('featurePageExpensesWorkflow3')
      ]}
      outcome={t('featurePageExpensesOutcome')}
      image={promoImage}
      imageAlt={t('featurePageExpensesTitle')}
      primaryLabel={t('marketingSeePricing')}
      secondaryLabel={t('marketingViewFeatures')}
      secondaryHref="/features"
      relatedTitle={t('marketingRelatedPagesTitle')}
      relatedCards={[
        {
          title: t('useCasePageExpenseTitle'),
          description: t('useCasePageExpenseSubtitle'),
          href: '/expense-claims-for-startups',
          image: leaveRequestsImage
        },
        {
          title: t('comparisonPageBambooHrTitle'),
          description: t('comparisonPageBambooHrSubtitle'),
          href: '/compare/bamboohr',
          image: profileImage
        },
        {
          title: t('useCasePageStartupTitle'),
          description: t('useCasePageStartupSubtitle'),
          href: '/startup-hr-software',
          image: promoImage
        }
      ]}
    />
  );
}

export function OnboardingFeaturePage() {
  const { t } = useTranslation();

  return (
    <MarketingLandingPage
      eyebrow={t('featurePageOnboardingEyebrow')}
      blogHref="https://blog.spothr.net/blog/how-to-onboard-a-new-employee-in-a-small-business/"
      blogLinkText="How to Onboard a New Employee in a Small Business"
      title={t('featurePageOnboardingTitle')}
      subtitle={t('featurePageOnboardingSubtitle')}
      workflowTitle={t('featurePageOnboardingWorkflowTitle')}
      workflowItems={[
        t('featurePageOnboardingWorkflow1'),
        t('featurePageOnboardingWorkflow2'),
        t('featurePageOnboardingWorkflow3')
      ]}
      outcome={t('featurePageOnboardingOutcome')}
      image={profileImage}
      imageAlt={t('featurePageOnboardingTitle')}
      primaryLabel={t('marketingSeePricing')}
      secondaryLabel={t('marketingViewFeatures')}
      secondaryHref="/features"
      relatedTitle={t('marketingRelatedPagesTitle')}
      relatedCards={[
        {
          title: t('useCasePageOnboardingTitle'),
          description: t('useCasePageOnboardingSubtitle'),
          href: '/employee-onboarding-software',
          image: profileImage
        },
        {
          title: t('comparisonPageSpreadsheetsTitle'),
          description: t('comparisonPageSpreadsheetsSubtitle'),
          href: '/compare/spreadsheets',
          image: leaveRequestsImage
        },
        {
          title: t('useCasePageStartupTitle'),
          description: t('useCasePageStartupSubtitle'),
          href: '/startup-hr-software',
          image: promoImage
        }
      ]}
    />
  );
}

export function ComparePersonioPage() {
  const { t } = useTranslation();

  return (
    <MarketingLandingPage
      eyebrow={t('comparisonPagePersonioEyebrow')}
      blogHref="https://blog.spothr.net/blog/best-personio-alternative-for-small-businesses-what-to-compare-before-you-switch/"
      blogLinkText="Best Personio Alternative for Small Businesses"
      title={t('comparisonPagePersonioTitle')}
      subtitle={t('comparisonPagePersonioSubtitle')}
      workflowTitle={t('comparisonPagePersonioWorkflowTitle')}
      workflowItems={[
        t('comparisonPagePersonioWorkflow1'),
        t('comparisonPagePersonioWorkflow2'),
        t('comparisonPagePersonioWorkflow3')
      ]}
      outcome={t('comparisonPagePersonioOutcome')}
      image={promoImage}
      imageAlt={t('comparisonPagePersonioTitle')}
      primaryLabel={t('marketingSeePricing')}
      secondaryLabel={t('featurePageLeaveEyebrow')}
      secondaryHref="/features/leave-management"
      relatedTitle={t('marketingRelatedPagesTitle')}
      relatedCards={[
        {
          title: t('comparisonPageBambooHrTitle'),
          description: t('comparisonPageBambooHrSubtitle'),
          href: '/compare/bamboohr',
          image: profileImage
        },
        {
          title: t('useCasePageStartupTitle'),
          description: t('useCasePageStartupSubtitle'),
          href: '/startup-hr-software',
          image: promoImage
        },
        {
          title: t('featurePageLeaveTitle'),
          description: t('featurePageLeaveSubtitle'),
          href: '/features/leave-management',
          image: leaveRemainingImage
        }
      ]}
    />
  );
}

export function CompareBambooHrPage() {
  const { t } = useTranslation();

  return (
    <MarketingLandingPage
      eyebrow={t('comparisonPageBambooHrEyebrow')}
      blogHref="https://blog.spothr.net/blog/best-bamboohr-alternative-for-startups-and-scaleups-what-to-compare-before-you-choose/"
      blogLinkText="Best BambooHR Alternative for Startups and Scaleups"
      title={t('comparisonPageBambooHrTitle')}
      subtitle={t('comparisonPageBambooHrSubtitle')}
      workflowTitle={t('comparisonPageBambooHrWorkflowTitle')}
      workflowItems={[
        t('comparisonPageBambooHrWorkflow1'),
        t('comparisonPageBambooHrWorkflow2'),
        t('comparisonPageBambooHrWorkflow3')
      ]}
      outcome={t('comparisonPageBambooHrOutcome')}
      image={profileImage}
      imageAlt={t('comparisonPageBambooHrTitle')}
      primaryLabel={t('marketingSeePricing')}
      secondaryLabel={t('featurePageOnboardingEyebrow')}
      secondaryHref="/features/onboarding"
      relatedTitle={t('marketingRelatedPagesTitle')}
      relatedCards={[
        {
          title: t('comparisonPagePersonioTitle'),
          description: t('comparisonPagePersonioSubtitle'),
          href: '/compare/personio',
          image: promoImage
        },
        {
          title: t('useCasePageOnboardingTitle'),
          description: t('useCasePageOnboardingSubtitle'),
          href: '/employee-onboarding-software',
          image: profileImage
        },
        {
          title: t('featurePageExpensesTitle'),
          description: t('featurePageExpensesSubtitle'),
          href: '/features/expense-claims',
          image: leaveRequestsImage
        }
      ]}
    />
  );
}

export function CompareSpreadsheetsPage() {
  const { t } = useTranslation();

  return (
    <MarketingLandingPage
      eyebrow={t('comparisonPageSpreadsheetsEyebrow')}
      blogHref="https://blog.spothr.net/blog/hr-software-for-startups-and-scaleups-what-to-look-for-before-you-outgrow-spreadsheets/"
      blogLinkText="HR Software for Startups and Scaleups: What to Look For Before You Outgrow Spreadsheets"
      title={t('comparisonPageSpreadsheetsTitle')}
      subtitle={t('comparisonPageSpreadsheetsSubtitle')}
      workflowTitle={t('comparisonPageSpreadsheetsWorkflowTitle')}
      workflowItems={[
        t('comparisonPageSpreadsheetsWorkflow1'),
        t('comparisonPageSpreadsheetsWorkflow2'),
        t('comparisonPageSpreadsheetsWorkflow3')
      ]}
      outcome={t('comparisonPageSpreadsheetsOutcome')}
      image={leaveRequestsImage}
      imageAlt={t('comparisonPageSpreadsheetsTitle')}
      primaryLabel={t('marketingSeePricing')}
      secondaryLabel={t('featurePageLeaveEyebrow')}
      secondaryHref="/features/leave-management"
      relatedTitle={t('marketingRelatedPagesTitle')}
      relatedCards={[
        {
          title: t('comparisonPagePersonioTitle'),
          description: t('comparisonPagePersonioSubtitle'),
          href: '/compare/personio',
          image: promoImage
        },
        {
          title: t('useCasePageLeaveTitle'),
          description: t('useCasePageLeaveSubtitle'),
          href: '/leave-management-for-small-teams',
          image: leaveRemainingImage
        },
        {
          title: t('useCasePageExpenseTitle'),
          description: t('useCasePageExpenseSubtitle'),
          href: '/expense-claims-for-startups',
          image: leaveRequestsImage
        }
      ]}
    />
  );
}

export function StartupHrSoftwarePage() {
  const { t } = useTranslation();

  return (
    <MarketingLandingPage
      eyebrow={t('useCasePageStartupEyebrow')}
      blogHref="https://blog.spothr.net/blog/hr-software-for-startups-and-scaleups-what-to-look-for-before-you-outgrow-spreadsheets/"
      blogLinkText="HR Software for Startups and Scaleups: What to Look For Before You Outgrow Spreadsheets"
      title={t('useCasePageStartupTitle')}
      subtitle={t('useCasePageStartupSubtitle')}
      workflowTitle={t('useCasePageStartupWorkflowTitle')}
      workflowItems={[
        t('useCasePageStartupWorkflow1'),
        t('useCasePageStartupWorkflow2'),
        t('useCasePageStartupWorkflow3')
      ]}
      outcome={t('useCasePageStartupOutcome')}
      image={promoImage}
      imageAlt={t('useCasePageStartupTitle')}
      primaryLabel={t('marketingCtaPrimary')}
      secondaryLabel={t('featuresPageEyebrow')}
      secondaryHref="/features"
      relatedTitle={t('marketingRelatedPagesTitle')}
      relatedCards={[
        {
          title: t('comparisonPagePersonioTitle'),
          description: t('comparisonPagePersonioSubtitle'),
          href: '/compare/personio',
          image: promoImage
        },
        {
          title: t('useCasePageExpenseTitle'),
          description: t('useCasePageExpenseSubtitle'),
          href: '/expense-claims-for-startups',
          image: leaveRequestsImage
        },
        {
          title: t('featurePageOnboardingTitle'),
          description: t('featurePageOnboardingSubtitle'),
          href: '/features/onboarding',
          image: profileImage
        }
      ]}
    />
  );
}

export function LeaveManagementForSmallTeamsPage() {
  const { t } = useTranslation();

  return (
    <MarketingLandingPage
      eyebrow={t('useCasePageLeaveEyebrow')}
      blogHref="https://blog.spothr.net/blog/leave-analytics-for-small-businesses/"
      blogLinkText="Leave Analytics for Small Businesses: What to Track Before Absence Becomes a Problem"
      title={t('useCasePageLeaveTitle')}
      subtitle={t('useCasePageLeaveSubtitle')}
      workflowTitle={t('useCasePageLeaveWorkflowTitle')}
      workflowItems={[
        t('useCasePageLeaveWorkflow1'),
        t('useCasePageLeaveWorkflow2'),
        t('useCasePageLeaveWorkflow3')
      ]}
      outcome={t('useCasePageLeaveOutcome')}
      image={leaveRemainingImage}
      imageAlt={t('useCasePageLeaveTitle')}
      primaryLabel={t('marketingSeePricing')}
      secondaryLabel={t('featurePageLeaveEyebrow')}
      secondaryHref="/features/leave-management"
      relatedTitle={t('marketingRelatedPagesTitle')}
      relatedCards={[
        {
          title: t('comparisonPageSpreadsheetsTitle'),
          description: t('comparisonPageSpreadsheetsSubtitle'),
          href: '/compare/spreadsheets',
          image: leaveRequestsImage
        },
        {
          title: t('useCasePageStartupTitle'),
          description: t('useCasePageStartupSubtitle'),
          href: '/startup-hr-software',
          image: promoImage
        },
        {
          title: t('featurePageLeaveTitle'),
          description: t('featurePageLeaveSubtitle'),
          href: '/features/leave-management',
          image: leaveRequestsImage
        }
      ]}
    />
  );
}

export function ExpenseClaimsForStartupsPage() {
  const { t } = useTranslation();

  return (
    <MarketingLandingPage
      eyebrow={t('useCasePageExpenseEyebrow')}
      blogHref="https://blog.spothr.net/blog/how-to-set-up-an-expense-claim-approval-process-for-a-small-business/"
      blogLinkText="How to Set Up an Expense Claim Approval Process for a Small Business"
      title={t('useCasePageExpenseTitle')}
      subtitle={t('useCasePageExpenseSubtitle')}
      workflowTitle={t('useCasePageExpenseWorkflowTitle')}
      workflowItems={[
        t('useCasePageExpenseWorkflow1'),
        t('useCasePageExpenseWorkflow2'),
        t('useCasePageExpenseWorkflow3')
      ]}
      outcome={t('useCasePageExpenseOutcome')}
      image={leaveRequestsImage}
      imageAlt={t('useCasePageExpenseTitle')}
      primaryLabel={t('marketingSeePricing')}
      secondaryLabel={t('featurePageExpensesEyebrow')}
      secondaryHref="/features/expense-claims"
      relatedTitle={t('marketingRelatedPagesTitle')}
      relatedCards={[
        {
          title: t('comparisonPageBambooHrTitle'),
          description: t('comparisonPageBambooHrSubtitle'),
          href: '/compare/bamboohr',
          image: profileImage
        },
        {
          title: t('useCasePageStartupTitle'),
          description: t('useCasePageStartupSubtitle'),
          href: '/startup-hr-software',
          image: promoImage
        },
        {
          title: t('featurePageExpensesTitle'),
          description: t('featurePageExpensesSubtitle'),
          href: '/features/expense-claims',
          image: promoImage
        }
      ]}
    />
  );
}

export function EmployeeOnboardingSoftwarePage() {
  const { t } = useTranslation();

  return (
    <MarketingLandingPage
      eyebrow={t('useCasePageOnboardingEyebrow')}
      blogHref="https://blog.spothr.net/blog/how-to-onboard-a-new-employee-in-a-small-business/"
      blogLinkText="How to Onboard a New Employee in a Small Business"
      title={t('useCasePageOnboardingTitle')}
      subtitle={t('useCasePageOnboardingSubtitle')}
      workflowTitle={t('useCasePageOnboardingWorkflowTitle')}
      workflowItems={[
        t('useCasePageOnboardingWorkflow1'),
        t('useCasePageOnboardingWorkflow2'),
        t('useCasePageOnboardingWorkflow3')
      ]}
      outcome={t('useCasePageOnboardingOutcome')}
      image={profileImage}
      imageAlt={t('useCasePageOnboardingTitle')}
      primaryLabel={t('marketingSeePricing')}
      secondaryLabel={t('featurePageOnboardingEyebrow')}
      secondaryHref="/features/onboarding"
      relatedTitle={t('marketingRelatedPagesTitle')}
      relatedCards={[
        {
          title: t('comparisonPageBambooHrTitle'),
          description: t('comparisonPageBambooHrSubtitle'),
          href: '/compare/bamboohr',
          image: profileImage
        },
        {
          title: t('comparisonPageSpreadsheetsTitle'),
          description: t('comparisonPageSpreadsheetsSubtitle'),
          href: '/compare/spreadsheets',
          image: leaveRequestsImage
        },
        {
          title: t('featurePageOnboardingTitle'),
          description: t('featurePageOnboardingSubtitle'),
          href: '/features/onboarding',
          image: profileImage
        }
      ]}
    />
  );
}

// Public address of the generic (org-agnostic) MCP endpoint. Mirrors
// MCP_PUBLIC_BASE_URL in the production infra config (https://spothr.net) plus
// the `/mcp` route served by the org-service.
const MCP_SERVER_URL = 'https://spothr.net/mcp';

export function McpSetupPage() {
  const { t } = useTranslation();

  return (
    <>
      <MarketingHeader pricingHref="/pricing" signupHref="/signup" />
      <Hero>
        <Container>
          <Typography variant="overline">{t('mcpSetupEyebrow')}</Typography>
          <Typography variant="h2">{t('mcpSetupHeroTitle')}</Typography>
          <Gap />
          <Typography variant="h6">{t('mcpSetupHeroSubtitle')}</Typography>
        </Container>
      </Hero>
      <Section>
        <Container>
          <SectionTitle>{t('mcpSetupServerTitle')}</SectionTitle>
          <Typography variant="body1">{t('mcpSetupServerBody')}</Typography>
          <Gap size={2} />
          <ServerAddress>{MCP_SERVER_URL}</ServerAddress>
        </Container>
      </Section>
      <Section alternate>
        <Container>
          <SectionTitle>{t('mcpSetupStepsTitle')}</SectionTitle>
          <NumberedList>
            <li>{t('mcpSetupStep1')}</li>
            <li>{t('mcpSetupStep2')}</li>
            <li>{t('mcpSetupStep3')}</li>
            <li>{t('mcpSetupStep4')}</li>
          </NumberedList>
          <Gap size={3} />
          <Typography variant="h6">{t('mcpSetupOtherTitle')}</Typography>
          <Gap />
          <Typography variant="body1">{t('mcpSetupOtherBody')}</Typography>
        </Container>
      </Section>
      <Section>
        <Container>
          <SectionTitle>{t('mcpSetupToolsTitle')}</SectionTitle>
          <Typography variant="body1">{t('mcpSetupToolsIntro')}</Typography>
          <Gap size={2} />
          <BulletList>
            <li>{t('mcpSetupToolLeave')}</li>
            <li>{t('mcpSetupToolExpenses')}</li>
            <li>{t('mcpSetupToolApprovals')}</li>
            <li>{t('mcpSetupToolTeam')}</li>
          </BulletList>
        </Container>
      </Section>
      <Section alternate>
        <Container>
          <SectionTitle>{t('mcpSetupSecurityTitle')}</SectionTitle>
          <Typography variant="body1">{t('mcpSetupSecurityBody')}</Typography>
        </Container>
      </Section>
    </>
  );
}

// Standalone, linkable versions of the legal documents that are otherwise only
// shown as in-checkout overlays. They reuse the shared content components so
// there is a single source of truth for the policy text.
export function PrivacyPolicyPage() {
  return (
    <>
      <MarketingHeader pricingHref="/pricing" signupHref="/signup" />
      <Section>
        <Container>
          <PrivacyPolicy />
        </Container>
      </Section>
    </>
  );
}

export function TermsOfServicePage() {
  return (
    <>
      <MarketingHeader pricingHref="/pricing" signupHref="/signup" />
      <Section>
        <Container>
          <TermsOfService />
        </Container>
      </Section>
    </>
  );
}
